import { collection, getDocs, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { firestore } from '../services/firebase';
import { PERMISSIONS } from '../shared/constants/permissions';
import type { Account } from '../types/account';

export const useDashboardMetadata = (account: Account | null | undefined) => {
  const [usersMap, setUsersMap] = useState<
    Record<string, { displayName: string; teamId?: string }>
  >({});
  const [teamsMap, setTeamsMap] = useState<Record<string, string>>({});

  // Fetch Teams for Mapping
  useEffect(() => {
    if (!account?.clientId) {
      return;
    }

    const fetchTeams = async () => {
      try {
        const teamsRef = collection(firestore, 'teams');
        const q = query(teamsRef, where('clientId', '==', account.clientId));
        const snapshot = await getDocs(q);
        const map: Record<string, string> = {};
        for (const doc of snapshot.docs) {
          map[doc.id] = doc.data().name || doc.data().teamName || 'Unknown Team';
        }
        setTeamsMap(map);
      } catch (_err) {
        // Ignore
      }
    };
    fetchTeams();
  }, [account?.clientId]);

  // Fetch Users for Mapping (Admins/Team Leads)
  useEffect(() => {
    if (!account?.clientId) {
      return;
    }

    const fetchUsers = async () => {
      try {
        const usersRef = collection(firestore, 'users');
        // Team Leads can only query users in their own team (security rule constraint)
        const q =
          account.cachedPermissions?.includes(PERMISSIONS.TENDER_MANAGE_TEAM) &&
          !account.cachedPermissions?.includes(PERMISSIONS.TENDER_VIEW_CLIENT)
            ? query(
                usersRef,
                where('clientId', '==', account.clientId),
                where('teamId', '==', account.teamId)
              )
            : query(usersRef, where('clientId', '==', account.clientId));

        const snapshot = await getDocs(q);
        const map: Record<string, { displayName: string; teamId?: string }> = {};
        for (const doc of snapshot.docs) {
          const data = doc.data();
          map[doc.id] = { displayName: data.displayName || data.email, teamId: data.teamId };
        }
        setUsersMap(map);
      } catch (_err) {
        // Ignore error
      }
    };
    fetchUsers();
  }, [account?.clientId, account?.cachedPermissions, account?.teamId]);

  return { usersMap, teamsMap };
};
